// @ts-check
/**
 * Záložky podle WAI-ARIA APG „Tabs" s automatickou aktivací (R-15 sekce 2, FR-2-47,
 * ui-checklist bod 17). Sdílené: volá se nad kořenem modulu `tabs.js` i nad podstromem jiného
 * modulu (budoucí karty a zásuvky).
 *
 * Očekávaný HTML ze serveru (pages/catalog/detail.gohtml): v `root` jeden `[role="tablist"]`
 * se záložkami `a[role="tab"][href][aria-controls]` BEZ `tabindex` a panely `[role="tabpanel"]`
 * s `tabindex="0"` a `hidden` mimo aktivní; záložka → panel výhradně přes `aria-controls`.
 * Aktivní záložku vykreslil server (`aria-selected="true"`); chybí-li, `initTabs` aktivuje
 * první (obrana — neznámý `?tab=` převádí handler). Bez JS jsou záložky odkazy a Enter
 * naviguje — stav je vždy v URL (bod 14).
 *
 * Co funkce dělá: nastaví pohyblivý `tabindex` (aktivní 0, ostatní −1; panel nese 0 ze šablony,
 * aby Tab z aktivní záložky vedl do panelu), a na kořeni deleguje `click` (jen levé tlačítko bez
 * modifikátorů: `preventDefault`, aktivace, `focus()` — Safari fokus kliknutím nedává) a `keydown`
 * ←/→ cyklicky, Home, End (jen bez Alt, Ctrl, Meta a Shift — Alt+← je zpět v prohlížeči; Esc
 * a Enter se neobsluhují). Přepnutí mění jen `aria-selected`, `tabindex` a `hidden` — vzhled
 * aktivní záložky dávají varianty `aria-selected:` v šabloně, třídy se nemění — a zapíše URL
 * `history.replaceState` s cestou a dotazem z `href` aktivované záložky (tvar adresy zná jen
 * server: Popis bez `tab`). `initTabs` URL nečte ani nemění. Žádný `fetch`, časovač ani texty;
 * posluchače bez `signal` (kořen žije s dokumentem). Fokus zůstává na záložce (`focus()` posune
 * rolující tablist do pohledu sám).
 */

/**
 * @typedef {object} TabsDeps
 * @property {(url: string) => void} [replaceState] zápis URL (výchozí `history.replaceState`
 *   okna dokumentu; jsdom testy vstřikují záznamník)
 */

/** Klávesy, které modul obsluhuje: název → výběr indexu z (aktuální, počet). */
const KEYS = {
  ArrowRight: (/** @type {number} */ i, /** @type {number} */ n) => (i + 1) % n,
  ArrowLeft: (/** @type {number} */ i, /** @type {number} */ n) => (i - 1 + n) % n,
  Home: () => 0,
  End: (/** @type {number} */ _i, /** @type {number} */ n) => n - 1,
};

/**
 * Záložka, do níž cíl události patří (klik na počet ve `<span>` patří záložce), jen uvnitř tablistu.
 * @param {EventTarget | null} target
 * @param {Element} tablist
 * @returns {HTMLAnchorElement | null}
 */
function tabFrom(target, tablist) {
  if (!(target instanceof Object) || !('closest' in target)) return null;
  const tab = /** @type {Element} */ (target).closest('a[role="tab"]');
  return tab && tablist.contains(tab) ? /** @type {HTMLAnchorElement} */ (tab) : null;
}

/**
 * @param {HTMLElement} root obal tablistu i panelů
 * @param {TabsDeps} [deps]
 */
export function initTabs(root, deps = {}) {
  const win = root.ownerDocument.defaultView;
  const tablist = root.querySelector('[role="tablist"]');
  if (!win || !tablist) return;
  const replaceState = deps.replaceState ?? ((url) => win.history.replaceState(null, '', url));

  /** @returns {HTMLAnchorElement[]} záložky v pořadí dokumentu */
  const tabs = () =>
    Array.from(tablist.querySelectorAll('a[role="tab"]')).filter(
      /** @returns {el is HTMLAnchorElement} */ (el) => el instanceof win.HTMLAnchorElement,
    );
  /** @param {Element} tab @returns {HTMLElement | null} panel podle `aria-controls` */
  const panelOf = (tab) => {
    const id = tab.getAttribute('aria-controls');
    const panel = id
      ? Array.from(root.querySelectorAll('[role="tabpanel"]')).find((p) => p.id === id)
      : null;
    return panel instanceof win.HTMLElement ? panel : null;
  };

  /**
   * Aktivuje záložku: `aria-selected`, pohyblivý `tabindex`, `hidden` panelů; `focus` a `url`
   * jen z události uživatele (init ani fokus ani URL nemění).
   * @param {HTMLAnchorElement} tab
   * @param {{ focus: boolean, url: boolean }} o
   */
  const select = (tab, o) => {
    for (const t of tabs()) {
      const active = t === tab;
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.tabIndex = active ? 0 : -1;
      const panel = panelOf(t);
      if (panel) panel.hidden = !active;
    }
    if (o.focus) tab.focus();
    if (o.url) {
      const u = new win.URL(tab.href);
      replaceState(u.pathname + u.search);
    }
  };

  const all = tabs();
  if (all.length === 0) return;
  select(all.find((t) => t.getAttribute('aria-selected') === 'true') ?? all[0], {
    focus: false,
    url: false,
  });

  root.addEventListener('click', (e) => {
    const tab = tabFrom(e.target, tablist);
    if (!tab || e.button !== 0 || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    e.preventDefault();
    if (tab.getAttribute('aria-selected') === 'true') return;
    select(tab, { focus: true, url: true });
  });

  root.addEventListener('keydown', (e) => {
    const tab = tabFrom(e.target, tablist);
    const pick = KEYS[/** @type {keyof typeof KEYS} */ (e.key)];
    if (!tab || !pick || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    e.preventDefault();
    const list = tabs();
    const next = list[pick(list.indexOf(tab), list.length)];
    if (next && next !== tab) select(next, { focus: true, url: true });
  });
}
